import classNames from 'classnames/bind';
import styles from './EbookActivityDetails.module.css';
import Book from '../Book';
import { useAppDispatch, useAppSelector } from '../../../hooks/useRedux';
import {
  projectIndexListChanged,
  selectMyProjectById,
  selectStudentProjectById,
} from '../../../features/analysisSlice';
import { openSelpubCloud } from '../../../utils/selpub';
import { useEffect, useState } from 'react';
import { analysisApiSlice } from '../../../services/analysis';
import { Project } from '../../../types/analysis';
import { getTopLevels, isMobile } from '../../../utils/util';
import {
  AUTHOR,
  LAST_LEARNING_DATE,
  LEARNING_TIME,
  OWNER,
  PRIMARY_STUDY_HOURS,
  PUBLISHER,
  STUDY_DAYS,
  COLLECTED_INFO_MESSAGE,
  RECENT_LEARNING_PAGE_KEYWORD,
  RECENT_LEARNING_PAGE_SUMMARY,
} from '../../../constants';
import LearningActivity from '../LearningActivity';
import TimeChart from './TimeChart';
import BarChart from './BarChart';
import KeywordList from './KeywordList';
import PageSummary from './PageSummary';
import RankingList from './RankingList';

const cx = classNames.bind(styles);

const getRankedItems = (data: { title: string; value: number }[]) => {
  const values = data.map((item) => item.value);
  const { highest, second } = getTopLevels(values);

  const rank1 = data
    .filter((item) => highest.includes(item.value))
    .map((item) => item.title);
  const rank2 = data
    .filter((item) => second.includes(item.value))
    .map((item) => item.title);
  const rank3 = data
    .filter(
      (item) =>
        item.value > 0 &&
        !highest.includes(item.value) &&
        !second.includes(item.value)
    )
    .map((item) => item.title);

  return { rank1, rank2, rank3 };
};

interface EbookActivityDetails {
  projectId: number;
  userId?: number;
}

export default function EbookActivityDetails({
  projectId,
  userId,
}: EbookActivityDetails) {
  const dispatch = useAppDispatch();
  const project = useAppSelector(
    (userId ? selectStudentProjectById : selectMyProjectById)(projectId)
  );

  const [getProject] =
    analysisApiSlice[
      userId ? 'useLazyGetClassProjectQuery' : 'useLazyGetProjectQuery'
    ]();

  const [projectData, setProjectData] = useState<Project>();

  const getProjectData = async () => {
    try {
      const { data } = await getProject({ projectId, userId });
      setProjectData(data);
    } catch (error) {
      console.error('Fail to get project', error);
    }
  };

  useEffect(() => {
    getProjectData();
  }, [projectId]);

  useEffect(() => {
    projectData && dispatch(projectIndexListChanged(projectData?.indexList));
  }, [projectData]);

  if (!project || !projectData) return null;

  const { title, publisher, author, owner, regDate, lastDate, today, total } =
    projectData;
  const bookMetadataList = [
    { title: PUBLISHER, value: publisher },
    { title: AUTHOR, value: author },
    { title: OWNER, value: owner },
  ];

  const timeZoneRanking = getRankedItems(projectData.timeZone.timeZone);
  const weekZoneRanking = getRankedItems(projectData.weekZone.timeZone);

  return (
    <div className={cx('dash_book_info')}>
      <div className={cx('info_panel', 'type_book')}>
        <div className={cx('binfo_box')}>
          <Book
            type="details"
            item={project}
            onClick={() => openSelpubCloud('viewer', projectId)}
            name="ebook-activity-details-book"
          />
          <div className={cx('binfo_detail')}>
            <h3 className={cx('binfo_title')}>{title}</h3>
            <div className={cx('panel_inner')}>
              {bookMetadataList.map(
                ({ title, value }) =>
                  value && (
                    <div key={title} className={cx('panel_row')}>
                      <span className={cx('sub')}>{title} : </span>
                      <span className={cx('detail')}>{value}</span>
                    </div>
                  )
              )}
            </div>
          </div>
        </div>

        <div className={cx('binfo_box')}>
          <div className={cx('binfo_date')}>
            <span>{LAST_LEARNING_DATE}</span>
            <span>{lastDate}</span>
          </div>
          <div className={cx('binfo_time')}>
            <span>{LEARNING_TIME}</span>
            <span>{total.totalTime}</span>
          </div>
        </div>
      </div>

      <div className={cx('visual_area_container')}>
        <div className={cx('visual_row')}>
          <div className={cx('visual_col')}>
            <div className={cx('visual_col_title')}>
              <i className={cx('icon_slot')}></i>
              <span className={cx('title')}>{PRIMARY_STUDY_HOURS}</span>
            </div>
            <div className={cx('visual_col_content')}>
              {isMobile ? (
                <RankingList {...timeZoneRanking} />
              ) : (
                <div className={cx('time_chart_wrapper')}>
                  <div className={cx('time_chart_container')}>
                    <div className={cx('time_chart')}>
                      <TimeChart data={projectData.timeZone.timeZone} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={cx('visual_col')}>
            <div className={cx('visual_col_title')}>
              <i className={cx('icon_week')}></i>
              <span className={cx('title')}>{STUDY_DAYS}</span>
            </div>
            <div className={cx('visual_col_content')}>
              {isMobile ? (
                <RankingList {...weekZoneRanking} />
              ) : (
                <div className={cx('bar_chart_wrapper')}>
                  <BarChart data={projectData.weekZone.timeZone} />
                </div>
              )}
            </div>
          </div>

          <div className={cx('visual_col')}>
            <div className={cx('visual_col_title')}>
              <i className={cx('icon_keyword')}></i>
              <span className={cx('title', 'beta')}>
                {RECENT_LEARNING_PAGE_KEYWORD}
              </span>
            </div>
            <div className={cx('visual_col_content')}>
              <KeywordList keywordData={projectData.keyword} />
            </div>
          </div>
        </div>

        <div className={cx('visual_row')}>
          <div className={cx('visual_col')}>
            <div className={cx('visual_col_title')}>
              <i className={cx('icon_study')}></i>
              <span className={cx('title', 'beta')}>
                {RECENT_LEARNING_PAGE_SUMMARY}
              </span>
            </div>
            <div className={cx('visual_col_content')}>
              <PageSummary summaryData={projectData.summary} />
            </div>
          </div>
        </div>
      </div>

      <div className={cx('info_panel', 'type_graph')}>
        <LearningActivity projectId={projectId} userId={userId} />
      </div>

      <p className={cx('desc')}>
        ※<span>{regDate}</span> ~ <span>{today}</span>
        {COLLECTED_INFO_MESSAGE}
      </p>
    </div>
  );
}
